import React, { useState, useEffect, useMemo } from 'react';
import { 
  ArrowLeft, 
  Phone, 
  MoreVertical, 
  FileText, 
  MessageSquare, 
  Share2,
  ChevronRight,
  Clock,
  Plus
} from 'lucide-react';
import { useLang } from '../../core/lang';
import LangKeys from '../../core/locales/keys';
import { getCustomerLedger } from '../../services/dashboardApi';
import ShareMessageLang from '../../core/lang/ShareMessageLang';
import EntryDetailsView from './EntryDetailsView';
import CustomerProfileView from './CustomerProfileView';
import CustomerReportView from './CustomerReportView';
import AddEntryModal from './AddEntryModal';

const LedgerView = ({ party, isSupplier, onBack, setActiveTab }) => {
  const { t } = useLang();
  const [loading, setLoading] = useState(true);
  const [ledgerData, setLedgerData] = useState(null);
  const [entries, setEntries] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addType, setAddType] = useState('get');
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [showReport, setShowReport] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [error, setError] = useState(null);

  const fetchLedger = async () => {
    try {
      setLoading(true);
      const data = await getCustomerLedger(party.id);
      if (data?.success) {
        setLedgerData(data.data?.summary || {});
        setEntries(data.data?.entries || []);
      } else {
        setError(data?.message || 'Failed to load');
      }
    } catch (err) {
      setError('Network error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLedger();
  }, [party.id]);

  const totals = useMemo(() => {
    if (!ledgerData) return { give: 0, get: 0, balance: 0 };
    return {
      give: parseFloat(ledgerData.total_give || ledgerData.total_paid || 0),
      get: parseFloat(ledgerData.total_get || ledgerData.total_received || 0),
      balance: parseFloat(ledgerData.total_balance || 0)
    };
  }, [ledgerData]);

  const buildShareMessage = () => {
    const totalBal = totals.balance;
    const smsLang = party.sms_lang || localStorage.getItem('sms_lang') || 'en';
    const balanceStatus = totalBal >= 0 ? ShareMessageLang.t(smsLang, 'credit') : ShareMessageLang.t(smsLang, 'debit');
    const balanceText = `₹${Math.abs(totalBal).toLocaleString()} ${balanceStatus}`;
    
    let shareLink = ledgerData?.share_link || (ledgerData?.share_code ? `https://khata.moxpal.com/share_view?code=${ledgerData.share_code}` : "https://khata.moxpal.com/app");

    return `${ShareMessageLang.t(smsLang, 'dear')} ${party.name},

${ShareMessageLang.t(smsLang, 'account_update')}
📌 *${ShareMessageLang.t(smsLang, 'total_due')}*: ${balanceText}

${ShareMessageLang.t(smsLang, 'view_full_account')}:
${shareLink}

— ${ShareMessageLang.t(smsLang, 'app_name')}`;
  };

  const handleWhatsappShare = () => {
    const message = buildShareMessage();
    let phone = (party.mobile || '').replace(/[^0-9]/g, '');
    if (phone.length === 10) phone = '91' + phone;
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(message)}`, '_blank');
  };

  const handleSmsShare = () => {
    const message = buildShareMessage();
    const phone = (party.mobile || '').replace(/[^0-9]/g, '');
    window.location.href = `sms:${phone}?body=${encodeURIComponent(message)}`;
  };

  if (selectedEntry) return <EntryDetailsView entry={selectedEntry} party={party} onBack={() => setSelectedEntry(null)} onRefresh={fetchLedger} />;
  if (showProfile) return <CustomerProfileView party={party} isSupplier={isSupplier} onBack={() => setShowProfile(false)} onRefresh={() => { fetchLedger(); onBack?.(); }} setActiveTab={setActiveTab} />;
  if (showReport) return <CustomerReportView party={party} entries={entries} onBack={() => setShowReport(false)} />;

  if (loading) return <div style={styles.loaderWrap}><div style={styles.loader}></div></div>;

  return (
    <div style={styles.container}>
      {/* HEADER */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <button style={styles.iconBtn} onClick={onBack}><ArrowLeft size={20} /></button>
          <div style={styles.partyInfo}>
            <h3 style={styles.headerName}>{party.name}</h3>
            <span style={styles.headerMobile}>{party.mobile}</span>
          </div>
        </div>
        <div style={styles.headerRight}>
          <button style={styles.actionIconBtn} onClick={() => window.open(`tel:${party.mobile}`)}><Phone size={18} color="#0284C7" /></button>
          <button style={styles.actionIconBtn} onClick={() => setShowProfile(true)}><MoreVertical size={18} /></button>
        </div>
      </div>

      <div style={styles.scrollContent}>
        {/* FEATURE TABS */}
        <div style={styles.featureTabs}>
          <button style={styles.featureTab} onClick={() => setShowReport(true)}>
            <div style={styles.featureIconWrap}><FileText size={18} color="#E11D48" /></div>
            <span style={styles.featureLabel}>{t(LangKeys.report)}</span>
          </button>
          <button style={styles.featureTab} onClick={handleWhatsappShare}>
            <div style={styles.featureIconWrap}><MessageSquare size={18} color="#25D366" /></div>
            <span style={styles.featureLabel}>{t(LangKeys.whatsapp)}</span>
          </button>
          <button style={styles.featureTab} onClick={handleSmsShare}>
            <div style={styles.featureIconWrap}><Share2 size={18} color="#3B82F6" /></div>
            <span style={styles.featureLabel}>{t(LangKeys.sms)}</span>
          </button>
        </div>

        {/* BALANCE CARD */}
        <div style={styles.balanceCard}>
          <span style={styles.balanceLabel}>{t(LangKeys.outstanding_balance)}</span>
          <h2 style={{
            ...styles.balanceMain, 
            color: totals.balance >= 0 ? '#16A34A' : '#DC2626'
          }}>
            ₹ {Math.abs(totals.balance).toLocaleString()}
          </h2>
          
          <div style={styles.balanceRow}>
            <div style={styles.balanceBox}>
              <span style={styles.subLabel}>{isSupplier ? t(LangKeys.paid) : t(LangKeys.you_gave)}</span>
              <span style={{...styles.subValue, color: '#DC2626'}}>₹ {totals.give.toLocaleString()}</span>
            </div>
            <div style={{...styles.balanceBox, alignItems: 'flex-end'}}>
              <span style={styles.subLabel}>{isSupplier ? t(LangKeys.received) : t(LangKeys.you_got)}</span>
              <span style={{...styles.subValue, color: '#16A34A'}}>₹ {totals.get.toLocaleString()}</span>
            </div>
          </div>
        </div>

        {/* ENTRIES LIST */}
        <div style={styles.listHeader}>
          <span>{t(LangKeys.entries)}</span>
        </div>

        <div style={styles.entriesList}>
          {entries.length === 0 ? (
            <div style={styles.emptyWrap}>{t(LangKeys.no_entries_found)}</div>
          ) : (
            entries.map((entry) => {
              const isGot = entry.entry_type === 'get';
              
              // Robust Date & Time Parsing (Matches Mobile App Logic)
              const parseEntryDateTime = (entryDate, createdAt) => {
                if (!entryDate) return { date: '', time: '' };
                
                // Use entry_date for the date part
                const datePart = new Date(entryDate.replace(' ', 'T'));
                const displayDate = datePart.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
                
                // Use created_at for the accurate time part
                const timeSource = createdAt || entryDate;
                const timePart = new Date(timeSource.replace(' ', 'T'));
                const displayTime = timePart.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
                
                return { date: displayDate, time: displayTime };
              };

              const { date: displayDate, time: displayTime } = parseEntryDateTime(entry.entry_date, entry.created_at);
              const runningBal = parseFloat(entry.running_balance || '0');
              
              return (
                <div key={entry.id} style={{
                  ...styles.entryRow,
                  borderLeft: `4px solid ${isGot ? '#16A34A' : '#DC2626'}`
                }} onClick={() => setSelectedEntry(entry)}>
                  <div style={styles.entryLeft}>
                    <div style={styles.entryNoteCol}>
                      <span style={styles.entryNote}>{entry.note || (isGot ? t(LangKeys.you_got_caps) : t(LangKeys.you_gave_caps))}</span>
                      <span style={styles.runningBalText}>Bal. ₹ {runningBal.toLocaleString()}</span>
                      <div style={styles.timeBox}>
                        <span>{displayDate} • {displayTime}</span>
                      </div>
                    </div>
                  </div>
                  <div style={styles.entryRight}>
                    <span style={{...styles.amountText, color: isGot ? '#16A34A' : '#DC2626'}}>
                      {isGot ? '+ ' : '- '}₹{parseFloat(entry.amount).toLocaleString()}
                    </span>
                    <ChevronRight size={14} color="#CBD5E1" />
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* BOTTOM ACTIONS */}
      <div style={styles.bottomBar}>
        <button 
          style={{...styles.addBtn, background: '#DC2626'}} 
          onClick={() => { setAddType('give'); setShowAddModal(true); }}
        >
          {isSupplier ? t(LangKeys.paid) : t(LangKeys.you_gave)} ₹
        </button>
        <button 
          style={{...styles.addBtn, background: '#16A34A'}} 
          onClick={() => { setAddType('get'); setShowAddModal(true); }}
        >
          {isSupplier ? t(LangKeys.received) : t(LangKeys.you_got)} ₹
        </button>
      </div>

      {showAddModal && (
        <AddEntryModal 
          isOpen={showAddModal} 
          onClose={() => setShowAddModal(false)} 
          party={party} 
          type={addType} 
          onSuccess={() => { setShowAddModal(false); fetchLedger(); }} 
        />
      )}
    </div>
  );
};

const styles = {
  container: { height: '100%', display: 'flex', flexDirection: 'column', background: '#F3F4F6' },
  header: { height: '64px', background: '#F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 16px', flexShrink: 0 },
  headerLeft: { display: 'flex', alignItems: 'center', gap: '12px' },
  iconBtn: { width: '40px', height: '40px', background: '#fff', border: 'none', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 2px 4px rgba(0,0,0,0.08)' },
  partyInfo: { display: 'flex', flexDirection: 'column' },
  headerName: { fontSize: '16px', fontWeight: 'bold', margin: 0 },
  headerMobile: { fontSize: '13px', color: '#666' },
  headerRight: { display: 'flex', gap: '8px' },
  actionIconBtn: { width: '40px', height: '40px', background: '#fff', border: 'none', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 2px 4px rgba(0,0,0,0.08)' },
  scrollContent: { flex: 1, overflowY: 'auto', padding: '0 16px' },
  
  featureTabs: { background: '#fff', borderRadius: '12px', padding: '12px 0', display: 'flex', justifyContent: 'space-around', margin: '8px 0', boxShadow: '0 4px 8px rgba(0,0,0,0.07)' },
  featureTab: { background: 'transparent', border: 'none', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', cursor: 'pointer' },
  featureIconWrap: { width: '22px', height: '22px', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  featureLabel: { fontSize: '12px', fontWeight: '600', color: '#444' },

  balanceCard: { background: '#fff', borderRadius: '16px', padding: '16px', margin: '8px 0', boxShadow: '0 4px 6px rgba(0,0,0,0.05)' },
  balanceLabel: { fontSize: '13px', color: '#666', fontWeight: '500' },
  balanceMain: { fontSize: '24px', fontWeight: 'bold', margin: '4px 0 14px 0' },
  balanceRow: { display: 'flex', justifyContent: 'space-between' },
  balanceBox: { display: 'flex', flexDirection: 'column', gap: '2px' },
  subLabel: { fontSize: '11px', color: '#888' },
  subValue: { fontSize: '14px', fontWeight: 'bold' },

  listHeader: { padding: '8px 0', fontSize: '15px', fontWeight: 'bold', color: '#000' },
  entriesList: { display: 'flex', flexDirection: 'column', gap: '8px', paddingBottom: '100px' },
  entryRow: { background: '#fff', borderRadius: '12px', padding: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' },
  entryLeft: { flex: 1 },
  entryNoteCol: { display: 'flex', flexDirection: 'column', gap: '2px' },
  entryNote: { fontSize: '13px', fontWeight: '500', color: '#333' },
  runningBalText: { fontSize: '12px', fontWeight: '600', color: '#374151' },
  timeBox: { fontSize: '11px', color: '#666' },
  entryRight: { display: 'flex', alignItems: 'center', gap: '8px' },
  amountText: { fontSize: '14px', fontWeight: '600' },

  bottomBar: { position: 'absolute', bottom: 0, left: 0, right: 0, background: '#fff', padding: '12px 16px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', boxShadow: '0 -3px 10px rgba(0,0,0,0.08)' },
  addBtn: { height: '48px', borderRadius: '8px', border: 'none', color: '#fff', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer' },
  
  loaderWrap: { height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  loader: { width: '30px', height: '30px', border: '3px solid #E2E8F0', borderTop: '3px solid #2563EB', borderRadius: '50%', animation: 'spin 1s linear infinite' },
  emptyWrap: { textAlign: 'center', padding: '40px 0', color: '#888', fontSize: '14px' }
};

export default LedgerView;
